<template>
  <v-card class="pa-0 overflow-hidden h-100">
    <reservation-history-dialog
      v-if="showHistoryDialogVisible && history"
      :reservation="r"
      :history="history"
      @close="onHistoryDialogClose"
      @resend-confirmation="onResendConfirmationFromHistory"
    />
    <reservation-results-dialog
      v-if="reservationResultsDialog"
      :visible="reservationResultsDialog"
      :title="$t('title.all_reservations_from_x', [contactWithReservations?.name ?? ''])"
      :reservations="contactReservations"
      @close="reservationResultsDialog = false"
    />
    <file-upload-dialog
      v-if="fileUploadDialog"
      :visible="fileUploadDialog"
      @close="fileUploadDialog = false"
      @input="attachmentsUploaded"
    />
    <contact-details-dialog
      v-if="contactDialogVisible"
      v-model="r.contact"
      :visible="contactDialogVisible"
      @close="contactDialogVisible = false"
    />
    <employee-dialog
      v-if="employeeDialogVisible"
      :visible="employeeDialogVisible"
      @close="employeeDialogVisible=false"
      @selected="submit"
    />
    <contact-message-dialog
      v-if="contactMessageDialog"
      :visible="contactMessageDialog"
      :reservation="r"
      @close="contactMessageDialog = false"
    />
    <!--  -->
    <confirmation-dialog
      v-if="unsavedChangesDialogVisible"
      :visible="unsavedChangesDialogVisible"
      :message="$t('message.confirm_leave_without_saving')"
      @confirm="discardChanges"
      @cancel="dismissChanges"
    />
    <!-- /// -->
    <confirmation-dialog
      v-if="warningsDialogVisible"
      :visible="warningsDialogVisible"
      :message="$t('message.alert_reservation_details_warning')"
      :confirm-title="$t('button.continue')"
      @confirm="submit"
      @close="warningsDialogVisible=false"
    >
      <p
        v-for="(text, index) in confirmationMessage.split('\n')"
        :key="index"
        :class="['py-0 my-0', {'pt-2': index === 0}]"
      >
        ∙ {{ text }}
      </p>
    </confirmation-dialog>
    <confirmation-dialog
      v-if="resendEmailConfirmation"
      visible
      :title="$t('label.resend_message')"
      :message="$t('message.confirm_resend_confirmation_email', [r.contact?.email])"
      @close="resendEmailConfirmation = false"
      @confirm="resendConfirmation"
    />
    <!-- Cancel Booking Confirmation Dialog -->
    <cancel-reservation-dialog
      v-if="cancelBookingConfirmationVisible"
      :reservation="r"
      @cancel-reservation="submit"
      @refund-reservation="requestReundAndSubmit"
      @close="cancelBookingConfirmationVisible=false"
    />
    <!-- Clear Contact confirmation  -->
    <confirmation-dialog
      v-if="clearContactConfirmationVisible"
      :visible="clearContactConfirmationVisible"
      :message="$t('message.confirm_clear_contact_existing')"
      :title="$t('label.edit_reservation')"
      @confirm="clearContact"
      @close="clearContactConfirmationVisible=false"
    />

    <!-- Duplicate Booking Confirmation Dialog -->
    <confirmation-dialog
      v-if="duplicateBookingDialogVisible"
      :visible="duplicateBookingDialogVisible"
      :message="$t('message.copy_booking_to')"
      :confirm-title="$t('button.copy')"
      @confirm="submitSaveOrDuplicate"
      @cancel="duplicateBookingCanceled"
      @close="duplicateBookingDialogVisible=false"
    >
      <date-picker-field
        content-class="pt-0"
        :value="duplicateBookingDate"
        hide-details
        :format-fn="formatReservationDate"
        @input="duplicateBookingDateChanged"
      />
    </confirmation-dialog>

    <v-app-bar
      fixed
      elevate-on-scroll
      scroll-target="#reservation-scroller"
    >
      <v-btn
        text
        outlined
        @click="close"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <v-spacer />

      <v-select
        :value="r.bookingType"
        outlined
        class="w-100 mr-2 ml-2 reservation-type-picker"
        :items="reservationTypeItems"
        :menu-props="{offsetY: true}"
        hide-details
        dense
        @input="reservationTypeChanged"
      />
      <v-spacer />
      <v-btn
        color="success"
        :disabled="(!isChanged && !isNew && !isUnread) || isErrors"
        @click="saveBooking"
      >
        <v-icon>mdi-check</v-icon>
      </v-btn>
    </v-app-bar>
    <v-sheet
      id="reservation-scroller"
      max-height="100%"
    >
      <v-form
        aria-autocomplete="off"
        autocomplete="off"
      >
        <validation-observer ref="observer">
          <v-container class="reservation-container">
            <div class="res-chip-wrapper">
              <reservation-chips
                class="pa-0 ma-0 mt-2"
                no-attachments
                no-service
                :reservation="r"
              />
            </div>
            <v-row align="center">
              <!-- Party Size -->
              <template v-if="!r.isBlock">
                <v-col
                  cols="12"
                  class="pb-1"
                >
                  <party-size-picker
                    class="warning-hint-capable"
                    :value="r.partySize"
                    :label="$t('label.party_size')"
                    :hint="[...errorMessages('partySize', true)].find(Boolean)"
                    @input="partySizeChanged"
                  />
                </v-col>
                <row-form-divider />
              </template>

              <!-- Start Date -->
              <v-col
                v-if="!r.isWalkin"
                key="startDate"
                cols="6"
                class="pb-0"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="date"
                >
                  <date-picker-field
                    :value="r.dateBegin"
                    :label="$t('label.reservation_date')"
                    hide-details
                    :error-messages="[...errors, ...errorMessages('date')]"
                    :hint="[...errorMessages('date', true)].find(Boolean)"
                    :format-fn="formatReservationDate"
                    @input="dateChanged"
                  />
                </validation-provider>
              </v-col>

              <!-- Start Time -->
              <v-col
                key="startTime"
                :cols="!r.isWalkin ? 3 : 6"
                class="pb-0"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="timeBegin"
                >
                  <time-picker-field
                    :disabled="r.isWalkin"
                    :value="timeBegin"
                    hide-details
                    :label="$t('label.reservation_starting_time')"
                    :error-messages="[...errors, ...errorMessages('timeBegin')]"
                    :hint="[...errorMessages('timeBegin', true)].find(Boolean)"
                    @input="timeBeginChanged"
                  />
                </validation-provider>
              </v-col>

              <!-- End Time -->
              <v-col
                key="endTime"
                :cols="!r.isWalkin ? 3 : 6"
                class="pb-0"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="timeEnd"
                >
                  <time-picker-field
                    :value="timeEnd"
                    hide-details
                    :label="$t('label.reservation_ending_time')"
                    :error-messages="[...errors, ...errorMessages('timeEnd')]"
                    :hint="[...errorMessages('timeEnd'), true].find(Boolean)"
                    @input="timeEndChanged"
                  >
                    <!-- Fixed Ending Time -->
                    <div class="justify-center d-flex w-100">
                      <v-switch
                        v-model="r.fixedEnding"
                        :label="$t('label.fixed_ending_time')"
                      />
                    </div>
                  </time-picker-field>
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                class="py-0 pb-1"
              >
                <v-input
                  class="warning-hint-capable"
                  persistent-hint
                  :error-messages="[...errorMessages('date'),
                                    ...errorMessages('timeBegin'),
                                    ...errorMessages('timeEnd')]"
                  :hint="[...errorMessages('date', true),
                          ...errorMessages('timeBegin', true),
                          ...errorMessages('timeEnd'), true].find(Boolean)"
                />
              </v-col>
              <row-form-divider />

              <!-- Section -->
              <v-col
                key="section"
                cols="6"
                class="pb-1"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="tab"
                  rules="required"
                >
                  <v-select
                    class="warning-hint-capable"
                    persistent-hint
                    :value="r.tab"
                    :items="tabs"
                    item-value="id"
                    item-text="name"
                    return-object
                    :label="$t('label.section')"
                    :error-messages="[...errors, ...errorMessages('tab')]"
                    :hint="[...errorMessages('tab', true)].find(Boolean)"
                    @input="tabChanged"
                  />
                </validation-provider>
              </v-col>

              <!-- Table -->
              <v-col
                key="table"
                cols="6"
                class="pb-1"
              >
                <validation-provider
                  v-slot="{ errors }"
                  vid="tabItems"
                >
                  <tab-item-menu>
                    <template #activator="{ on }">
                      <v-text-field
                        class="warning-hint-capable"
                        persistent-hint
                        :value="tabItemsText"
                        :label="$t('label.reservation_table')"
                        readonly
                        :error-messages="[...errors, ...errorMessages('tabItems')]"
                        :hint="[...errorMessages('tabItems', true)].find(Boolean)"
                        :loading="isSuggestingTabItems"
                        v-on="on"
                      />
                    </template>
                  </tab-item-menu>
                </validation-provider>
              </v-col>

              <!-- Name, Phone, Email -->
              <template v-if="!r.isWalkin && !r.isBlock">
                <row-form-divider />
                <v-col
                  cols="12"
                  class="pb-0"
                >
                  <!-- Autofocus only on larger screens, so softkeyboard is not immediately presented -->
                  <contact-autocomplete
                    :contact="r.contact"
                    :focus-name="isNew && $vuetify.breakpoint.mdAndUp"
                    @update-contact="updateContact"
                    @update-name="updateName"
                    @update-email="updateEmail"
                    @update-phone="updatePhone"
                    @clear="clearContactClicked"
                  />
                  <v-sheet
                    v-if="r.contact.notes"
                    style="max-height: 80px"
                    class="text-caption ellipsize-2 mb-0"
                  >
                    <span class="font-weight-medium">{{ $t('label.contact_note') }}: </span>
                    {{ r.contact.notes }}
                  </v-sheet>
                </v-col>
                <v-col
                  cols="12"
                  class="pt-2 text-right"
                >
                  <!-- Resend Confirmation Email -->
                  <tooltip-icon-button
                    v-if="reservationStatusAllowsResendEmail"
                    key="resend-confirmation-email"
                    :tooltip-title="$t('label.resend_confirmation_email')"
                    button-class="mr-1"
                    color="primary"
                    :icon="false"
                    outlined
                    x-small
                    @click="resendEmailConfirmation = true"
                  >
                    <v-icon small>
                      mdi-email-check-outline
                    </v-icon>
                  </tooltip-icon-button>

                  <!-- Send message -->
                  <tooltip-icon-button
                    key="message-guest"
                    :tooltip-title="$t('label.message_guest')"
                    button-class="mr-1"
                    color="primary"
                    outlined
                    :icon="false"
                    x-small
                    :disabled="!contactMessageDialogEnabled"
                    @click="contactMessageDialog = true"
                  >
                    <v-icon small>
                      mdi-email-fast-outline
                    </v-icon>
                  </tooltip-icon-button>

                  <!-- List all reservations for this contact -->
                  <tooltip-icon-button
                    v-if="r?.contact?.reservationCount !== undefined && r?.contact?.reservationCount > 0"
                    key="list-all-reservations-for-this-contact"
                    :tooltip-title="$t('label.list_all_reservations_for_this_contact')"
                    button-class="mr-1"
                    color="primary"
                    outlined
                    x-small
                    :icon="false"
                    @click="showContactReservations"
                  >
                    <div class="d-flex align-center flex-row">
                      <v-icon small>
                        mdi-account-search-outline
                      </v-icon>
                      <div
                        class="ml-1"
                        style="line-height: 1;"
                      >
                        {{ `(${r?.contact?.reservationCount})` }}
                      </div>
                    </div>
                  </tooltip-icon-button>

                  <!-- Contact details -->
                  <v-btn
                    outlined
                    color="primary"
                    x-small
                    @click="contactDialogVisible = true"
                  >
                    {{ $t('label.contact_details') }}
                  </v-btn>
                </v-col>
              </template>

              <row-form-divider />

              <!-- Reservation Note -->
              <v-col
                cols="12"
                class="pb-0"
              >
                <validation-provider
                  v-slot="{ errors }"
                  rules="max:3999"
                >
                  <v-textarea
                    ref="note"
                    v-model="r.notes"
                    :error-messages="errors"
                    :hide-details="!errors || errors.length === 0"
                    :label="$t('label.notes')"
                    rows="3"
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                class="pt-2 text-right"
              >
                <tag-note-menu @selected="addTagNote">
                  <template #activator="{ on }">
                    <v-btn
                      outlined
                      color="primary"
                      x-small
                      v-on="on"
                    >
                      {{ $t('button.insert_tag') }}
                    </v-btn>
                  </template>
                </tag-note-menu>
              </v-col>

              <template v-if="!r.isBlock">
                <row-form-divider />

                <!-- Reservation Status -->
                <v-col
                  class="pb-2"
                  cols="6"
                >
                  <reservation-status-select v-model="r.status" />
                </v-col>

                <!-- Label -->
                <v-col
                  class="pb-2"
                  cols="6"
                >
                  <reservation-item-select
                    v-model="r.label"
                    :items="labels"
                    :label="$t('label.label')"
                  />
                </v-col>

                <!-- Service -->
                <v-col
                  class="pt-0 pb-0"
                  cols="12"
                >
                  <reservation-item-select
                    v-model="r.service"
                    :items="services"
                    :label="$t('label.service')"
                  />
                </v-col>

                <v-col
                  v-if="isInOptionReservation"
                  cols="12"
                >
                  <validation-provider
                    v-slot="{ errors }"
                    rules="lteqDateTime:@date"
                  >
                    <date-time-picker-field
                      v-model="r.dateExpiration"
                      :format-fn="fmtInOptionDate"
                      :label="$t('label.option_expires_at')"
                      :min-date="minExpirationDate"
                      :max-date="r.dateBegin"
                      :default-time="defaultExpirationTime"
                      :error-messages="errors"
                      clearable
                    />
                  </validation-provider>
                </v-col>

                <!-- Flagged -->
                <v-col
                  class="pt-1 pb-0"
                  cols="6"
                >
                  <v-checkbox
                    v-model="r.isFlagged"
                    :label="$t('label.flagged')"
                  />
                </v-col>

                <!-- Starred -->
                <v-col
                  class="pt-1 pb-0"
                  cols="6"
                >
                  <v-checkbox
                    v-model="r.isStarred"
                    :label="$t('label.starred')"
                  />
                </v-col>
              </template>

              <!-- IN OPTION -->

              <!-- Attachments -->
              <row-form-divider />
              <v-col
                cols="12"
                class="py-0"
              >
                <v-expansion-panels
                  v-if="r.attachments.length > 0"
                  v-model="attachmentsPanels"
                  flat
                >
                  <v-expansion-panel class="elevation-0">
                    <v-expansion-panel-header class="pa-0">
                      {{ $t('label.attachments') }} ({{ r.attachments.length }})
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div
                        v-for="(attachment, index) in r.attachments"
                        :key="index"
                        class="d-flex align-baseline"
                      >
                        <a
                          class="d-block w-100"
                          :href="attachment.url"
                          target="_blank"
                        >{{ attachment.name }}</a>

                        <v-spacer />
                        <v-btn
                          icon
                          @click="deleteAttachment(index)"
                        >
                          <v-icon small>
                            mdi-delete
                          </v-icon>
                        </v-btn>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
                <v-btn
                  class="mt-3"
                  outlined
                  block
                  small
                  color="primary"
                  @click="fileUploadDialog = true"
                >
                  {{ $t('button.add_attachment') }}
                </v-btn>
              </v-col>
              <row-form-divider />

              <!-- Copy Button -->
              <v-col
                v-if="!isNew"
                cols="12"
                class="pt-3 pb-0"
              >
                <v-btn
                  outlined
                  block
                  small
                  color="primary"
                  @click="duplicateBooking"
                >
                  {{ $t('label.save_as_copy') }}
                </v-btn>
              </v-col>

              <!-- Cancel Button -->
              <v-col
                v-if="!isNew"
                cols="12"
                class="pt-3 pb-0"
              >
                <v-btn
                  outlined
                  block
                  small
                  color="red"
                  @click="cancelBooking"
                >
                  {{ $t('button.cancel_this_booking') }}
                </v-btn>
              </v-col>
              <!-- /// -->

              <!-- Footer Info Block -->
              <v-col
                class="pt-2 pb-0"
                cols="12"
              >
                <!-- Date Created -->
                <p
                  v-if="dateCreated"
                  class="text--secondary text-center text-caption ma-0 pb-0"
                >
                  {{ $t('label.created') }} <b>{{ dateCreated }} {{ employeeCreated }}</b>
                </p>

                <!-- Date Edited -->
                <p
                  v-if="dateEdited"
                  class="text--secondary text-center text-caption ma-0 pt-0 pb-0"
                >
                  {{ $t('label.last_edit') }} <b>{{ dateEdited }} {{ employeeEdited }}</b>
                </p>

                <!-- History -->
                <p
                  v-if="!isNew"
                  class="text--secondary text-center text-caption ma-0 pt-0 pb-0"
                >
                  <a
                    href="#"
                    @click="showHistory"
                  >{{ $t('label.show_history') }}</a>
                </p>

                <!-- Reservation Code -->
                <p
                  v-if="r.code"
                  class="text--secondary text-center text-caption ma-0 pt-0 pb-0"
                >
                  {{ $t('label.code') }} <b>{{ `#${r.code}` }}</b>
                </p>
              </v-col>
            </v-row>
          </v-container>
        </validation-observer>
      </v-form>
    </v-sheet>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue';
import Reservation, { ReservationSource, ReservationStatus, ReservationType } from '@/model/Reservation';
import Tab from '@/model/Tab';
import TabItem from '@/model/TabItem';
import ContactMessageDialog from '@/components/views/reservation/ContactMessageDialog.vue';
import ReservationHistoryDialog from '@/components/views/reservation/ReservationHistoryDialog.vue';
import EmployeeDialog from '@/components/views/reservation/EmployeeDialog.vue';
import ContactDetailsDialog from '@/components/views/reservation/ContactDetailsDialog.vue';
import FileUploadDialog from '@/components/views/reservation/FileUploadDialog.vue';
import ReservationResultsDialog from '@/components/views/reservation/ReservationResultsDialog.vue';
import ContactAutocomplete from '@/components/views/reservation/ContactAutocomplete.vue';
import DatePickerField from '@/components/pickers/DatePickerField.vue';
import DateTimePickerField from '@/components/pickers/DateTimePickerField.vue';
import PartySizePicker from '@/components/pickers/PartySizePicker.vue';
import CancelReservationDialog from '@/components/dialogs/CancelReservationDialog.vue';
import TabItemMenu from '@/components/views/reservation/TabItemMenu.vue';
import TagNoteMenu from '@/components/views/reservation/TagNoteMenu.vue';
import TagNote from '@/model/TagNote';
import { mapActions } from 'vuex';
import { performDispatchAction, performSaveAction } from '@/services/vue-utils';
import unsavedGuard from '@/mixins/unsaved-guard';
import Contact from '@/model/Contact';
import { tryCloseEditReservation } from '@/services/reservation-edit-utils';
import {
  ReservationError, reservationErrorTexts, isReservationErrors, Warnings, ReservationErrorType,
  DateBeginErrors, DateEndErrors, TabErrors, TabItemsErrors, DateErrors,
  NameErrors, PhoneErrors, PartySizeErrors, EmailErrors,
} from '@/services/reservation-validation';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import Service from '@/model/Service';
import {
  dateByAddingMinutes,
  dateCeiledToMinutes, isDateSameIgnoringTime, timeStringFromDate,
} from '@/services/time-utils';
import Attachment from '@/model/Attachment';
import { History } from '@/services/reservation-history';
import showConfirmationDialog from '@/components/dialogs/confirmation-dialog';
import Label from '@/model/Label';
import { WaitingListReservationStatuses } from '@/util/status';
import store from '@/store/store';
import TooltipIconButton from '@/components/buttons/TooltipIconButton.vue';
import ReservationStatusSelect from './ReservationStatusSelect.vue';
import ReservationItemSelect from './ReservationItemSelect.vue';
import ReservationChips from '../guests/ReservationChips.vue';

enum SubmitBookingType {
  none,
  save,
  cancel,
  duplicate,
}

const HISTORY_FAST_INTERVAL_MS = 1_000;
const HISTORY_SLOW_INTERVAL_MS = 10_000;
const HISTORY_FAST_TICKS = 20;
const HISTORY_POLL_MAX = 100;

export default Vue.extend({
  name: 'Reservation',
  components: {
    CancelReservationDialog,
    ReservationChips,
    EmployeeDialog,
    PartySizePicker,
    TabItemMenu,
    ContactDetailsDialog,
    TagNoteMenu,
    ConfirmationDialog,
    ContactAutocomplete,
    FileUploadDialog,
    ReservationResultsDialog,
    ReservationHistoryDialog,
    ReservationStatusSelect,
    ReservationItemSelect,
    ContactMessageDialog,
    DatePickerField,
    TooltipIconButton,
    DateTimePickerField,
  },
  mixins: [unsavedGuard],
  props: {
    isVisible: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      contactMessageDialog: false,
      employeeDialogVisible: false,
      contactDialogVisible: false,
      warningsDialogVisible: false,
      cancelBookingConfirmationVisible: false,
      clearContactConfirmationVisible: false,
      duplicateBookingDialogVisible: false,
      duplicateBookingDate: new Date(),
      confirmationMessage: '',
      resendEmailConfirmation: false,
      submitBookingType: SubmitBookingType.none,
      attachmentsPanels: undefined as number | undefined,
      fileUploadDialog: false,

      history: null as History | null,
      showHistoryDialogVisible: false,
      historyPollTimer: null as ReturnType<typeof setTimeout> | null,
      historyPollCount: 0,

      contactWithReservations: null as Contact | null,
      contactReservations: null as Reservation[] | null,
      reservationResultsDialog: false,

      extraErrorMessages: new Map<string, string[]>(),

      reservationTypes: [
        { text: this.$i18n.tc('code.booking_type.reservation'), value: ReservationType.Reservation },
        { text: this.$i18n.tc('code.booking_type.walkin'), value: ReservationType.Walkin },
        { text: this.$i18n.tc('code.booking_type.waitlist'), value: ReservationType.Waitlist },
        { text: this.$i18n.tc('code.booking_type.block'), value: ReservationType.Block },
      ],
    };
  },
  computed: {
    r(): Reservation {
      return this.$tstore.getters.editReservation ?? Reservation.emptyReservation();
    },
    oldr(): Reservation {
      return this.$tstore.getters.originalReservation ?? Reservation.emptyReservation();
    },
    isChanged(): boolean {
      return this.$tstore.getters.isChangedEditReservation || this.isSuggestedTabItems;
    },
    isNew(): boolean {
      return this.r.isNew;
    },
    isUnread(): boolean {
      return this.r.isUnread;
    },
    isErrors(): boolean {
      return isReservationErrors(this.validationErrors);
    },
    tabs(): Tab[] { return this.$tstore.state.settings.tabs; },
    suggestedTabItems(): TabItem[] | null {
      return this.$tstore.getters.suggestedTabItems;
    },
    isSuggestedTabItems(): boolean {
      return this.suggestedTabItems !== null && this.suggestedTabItems.length > 0;
    },
    isSuggestingTabItems(): boolean {
      return this.$tstore.getters.isSuggestingTabItems;
    },
    tabItemsText(): string {
      if (this.r.tabItems.length !== 0) {
        return this.r.tabItems.map((t) => t.no).join(', ');
      }
      if (this.suggestedTabItems !== null && this.suggestedTabItems.length > 0) {
        return this.suggestedTabItems.map((t) => t.no).join(', ');
      }
      if (!this.tableAllocationDisabled && !this.r.isBlock) return this.$i18n.tc('label.automatic');
      return '';
    },
    timeBegin(): string {
      return timeStringFromDate(this.r.dateBegin) ?? '';
    },
    timeEnd(): string {
      return timeStringFromDate(this.r.dateEnd) ?? '';
    },
    employeeNameIsNeeded(): boolean {
      return this.$tstore.getters.employeeNameIsNeeded;
    },
    tableAllocationDisabled(): boolean {
      return !(this.$tstore.state.settings.accountSettings.tableAllocation ?? false);
    },
    dateCreated(): string | null {
      if (!this.r.dateCreated) return null;
      return this.$localized.smartDateText(this.r.dateCreated);
    },
    dateEdited(): string | null {
      if (!this.r.dateEdited) return null;
      return this.$localized.smartDateText(this.r.dateEdited);
    },
    employeeCreated(): string | null {
      if (this.r.reservationType === ReservationSource.Online) {
        return `(${this.r.reservationCampaign || this.$i18n.tc('code.reservation_source.online')})`;
      }
      if (!this.r.employeeCreated) return null;
      return `(${this.r.employeeCreated?.name})`;
    },
    employeeEdited(): string | null {
      if (!this.r.employeeEdited) return null;
      return `(${this.r.employeeEdited?.name})`;
    },
    validationErrors(): ReservationError[] {
      const errors: ReservationError[] = [];
      errors.push(...this.$tstore.getters.reservationValidationErrors);
      errors.push(...this.$tstore.getters.reservationAvailabilityValidationErrors);
      if (this.overbookedTabItems.length > 0) {
        errors.push({ type: ReservationErrorType.TabItemsOverbooked });
      }
      if (this.suggestedTabItems !== null && this.suggestedTabItems.length === 0) {
        errors.push({ type: ReservationErrorType.AvailabilityNoAllocation });
      }
      if (this.tableAllocationDisabled && this.r.tabItems.length === 0) {
        errors.push({ type: ReservationErrorType.TabItemsMissing });
      }
      return errors;
    },
    overbookedTabItems(): TabItem[] {
      return this.$tstore.getters.reservationOverbookedTabItems;
    },
    unsavedChangesDialogVisible(): boolean {
      return this.$tstore.getters.isConfirmCloseEditReservation;
    },
    contactMessageDialogEnabled(): boolean {
      return this.$tstore.getters.isReservationMessageAllowed;
    },
    services(): Service[] {
      return this.$tstore.state.settings.services;
    },
    labels(): Label[] {
      const { labels } = this.$tstore.state.settings;
      const oldl = this.oldr.label;
      if (!this.oldr.label) return labels;
      return labels.find((l) => l.id === oldl?.id) ? labels : [oldl!, ...labels];
    },
    reservationTypeItems() {
      if (this.r.bookingType === ReservationType.Walkin) { return this.reservationTypes; }
      return this.reservationTypes.filter((ri) => !(ri.value === ReservationType.Walkin
      && !isDateSameIgnoringTime(new Date(), this.$tstore.state.update.date)));
    },
    storeDate() {
      return this.$tstore.state.update.date;
    },
    isInOptionReservation() {
      if (this.r.status === ReservationStatus.NonConfirmed && !this.r.isWalkin) return true;
      return false;
    },
    minExpirationDate(): Date {
      return dateCeiledToMinutes(new Date(), 15);
    },
    defaultExpirationTime(): string | undefined {
      const defaultDate = dateCeiledToMinutes(dateByAddingMinutes(new Date(), 120), 15);
      return timeStringFromDate(defaultDate < this.r.dateBegin ? defaultDate : this.r.dateBegin) ?? undefined;
    },
    reservationStatusAllowsResendEmail(): boolean {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      return !!this.r.contact?.email
        && this.r.dateBegin >= startOfToday
        && [ReservationStatus.Planned,
          ReservationStatus.Confirmed,
          ReservationStatus.NonConfirmed,
          ReservationStatus.WaitingList].includes(this.r.status);
    },
  },
  watch: {
    storeDate() {
      if (this.r.bookingType === ReservationType.Walkin
       && !isDateSameIgnoringTime(new Date(), this.storeDate)) {
        this.r.bookingType = ReservationType.Reservation;
        this.reservationTypeChanged(ReservationType.Reservation);
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.keyDown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.keyDown);
    this.stopHistoryPolling();
  },
  methods: {
    ...mapActions([
      'sendEditReservation', 'confirmCloseEditReservation', 'closeEditReservation']),
    keyDown(evt: KeyboardEvent) {
      if (!this.isVisible || evt.target !== document.body) { return; }
      switch (evt.keyCode) {
        case 13:
          this.saveBooking();
          break;
        case 27:
          this.close();
          break;
        default:
          break;
      }
    },
    dateChanged(date: Date) {
      this.$tstore.dispatch('setReservationDate', date);
      this.$tstore.dispatch('keepReservationChangeToDate', date);
    },
    timeBeginChanged(time: string) {
      this.$tstore.dispatch('setReservationTimeBegin', time);
    },
    timeEndChanged(time: string) {
      this.$tstore.dispatch('setReservationTimeEnd', time);
    },
    tabChanged(tab: Tab) {
      this.$tstore.dispatch('setReservationTab', tab);
    },
    partySizeChanged(partySize: number) {
      this.$tstore.dispatch('setReservationPartySize', partySize);
    },
    reservationTypeChanged(reservationType: ReservationType) {
      this.$tstore.dispatch('setReservationType', reservationType);
    },
    clearContact() {
      this.$tstore.dispatch('setReservationContact', new Contact());
      this.clearContactConfirmationVisible = false;
    },
    errorMessages(key: string, warning: boolean = false) {
      const errors = this.validationErrors.filter((e) => (
        warning ? Warnings.includes(e.type) : !Warnings.includes(e.type)));

      let messages: string[] = [];

      switch (key) {
        case 'partySize': messages = reservationErrorTexts(PartySizeErrors, errors); break; // not needed
        case 'date': messages = reservationErrorTexts(DateErrors, errors); break;// not needed
        case 'tab': messages = reservationErrorTexts(TabErrors, errors); break;
        case 'tabItems': messages = reservationErrorTexts(TabItemsErrors, errors); break;
        case 'timeBegin': messages = reservationErrorTexts(DateBeginErrors, errors); break;
        case 'timeEnd': messages = reservationErrorTexts(DateEndErrors, errors); break;
        case 'name': messages = reservationErrorTexts(NameErrors, errors); break; // not needed
        case 'phone': messages = reservationErrorTexts(PhoneErrors, errors); break; // not needed
        case 'email': messages = reservationErrorTexts(EmailErrors, errors); break; // not needed
        default:
      }

      const extraMessages = this.extraErrorMessages.get(key) ?? [];
      return [...extraMessages, ...messages];
    },
    async cancelBooking() {
      this.submitBookingType = SubmitBookingType.cancel;
      this.cancelBookingConfirmationVisible = true;
    },
    async duplicateBooking() {
      this.submitBookingType = SubmitBookingType.duplicate;
      this.duplicateBookingDate = this.r.dateBegin;
      this.duplicateBookingDialogVisible = true;
    },
    async saveBooking() {
      this.submitBookingType = SubmitBookingType.save;
      this.submitSaveOrDuplicate();
    },
    async submitSaveOrDuplicate() {
      this.duplicateBookingDialogVisible = false;

      if (isReservationErrors(this.validationErrors)) {
        return;
      }

      // show confirmation for warnings
      const warninigs = reservationErrorTexts(Warnings, this.validationErrors);
      if (warninigs.length) {
        this.confirmationMessage = warninigs.join('\n');
        this.warningsDialogVisible = true;
        return;
      }

      if (this.oldr.status !== ReservationStatus.Cancelled && this.r.status === ReservationStatus.Cancelled) {
        this.cancelBookingConfirmationVisible = true;
        return;
      }

      this.submit();
    },
    async requestReundAndSubmit() {
      this.r.isRefundRequested = true;
      this.submit();
    },
    async submit() {
      this.cancelBookingConfirmationVisible = false;
      this.warningsDialogVisible = false;

      // show employee dialog (do not show for walkin reservations - Walkin reservation asks for employee name #29)
      if (!this.employeeDialogVisible && this.employeeNameIsNeeded && !this.r.isWalkin) {
        this.employeeDialogVisible = true;
        return;
      }
      this.employeeDialogVisible = false;

      // save
      let ok = false;
      const p = {
        cancel: false,
        duplicate: false,
        dateBegin: this.duplicateBookingDate,
        suggestedTabItems: this.suggestedTabItems,
      };

      if (this.submitBookingType === SubmitBookingType.cancel) p.cancel = true;
      if (this.submitBookingType === SubmitBookingType.duplicate) p.duplicate = true;

      ok = await performSaveAction(
        this.$refs.observer,
        async () => this.sendEditReservation(p),
      );
      if (ok) this.closeEditReservation();
      else {
        // for duplicate booking
        this.$tstore.dispatch('keepReservationChangeToDate', this.r.dateBegin);
      }
    },
    async close() {
      await tryCloseEditReservation();
    },
    discardChanges() {
      this.confirmCloseEditReservation(true);
    },
    dismissChanges() {
      this.confirmCloseEditReservation(false);
    },
    addTagNote(tag: TagNote) {
      const fieldVue = this.$refs.note as Vue;
      const field = fieldVue.$refs.input as HTMLTextAreaElement;

      const tagValue = ` ${tag.dotText} `;

      // FIXME: Check this on IE Browser
      if (field.selectionStart || field.selectionStart === 0) {
        const startPos = field.selectionStart;
        const endPos = field.selectionEnd;
        const text = this.r.notes || '';

        this.r.notes = text.substring(0, startPos)
        + tagValue
        + text.substring(endPos, text.length);
      }
    },
    formatReservationDate(d: Date): string | null {
      return this.$localized.dayMonthText(d);
    },
    duplicateBookingDateChanged(date: Date) {
      this.duplicateBookingDate = date;
      this.$tstore.dispatch('keepReservationChangeToDate', date);
    },
    duplicateBookingCanceled() {
      this.$tstore.dispatch('keepReservationChangeToDate', this.r.dateBegin);
    },
    deleteAttachment(index: number) {
      showConfirmationDialog(
        this.$i18n.tc('label.attachments'),
        this.$i18n.tc('message.confirm_delete_attachment'),
        {
          title: this.$i18n.tc('button.yes'),
          action: () => {
            const attachments = [...this.r.attachments];
            attachments.splice(index, 1);
            this.$tstore.dispatch('setReservationAttachments', attachments);
          },
        },
        { title: this.$i18n.tc('button.cancel') },
      );
    },
    attachmentsUploaded(uploads: { name: string, url: string }[]) {
      console.log('attachmentsUploaded: ', uploads);

      const attachments = [...this.r.attachments];

      uploads.forEach((f) => {
        const a = new Attachment();
        a.name = f.name;
        a.url = f.url;
        attachments.push(a);
      });
      this.$tstore.dispatch('setReservationAttachments', attachments);

      this.attachmentsPanels = 0;
    },
    async showContactReservations() {
      const reservations = await performDispatchAction<Reservation[]>('getContactReservations', undefined);
      if (!reservations) return;
      this.contactWithReservations = this.r.contact;
      this.contactReservations = reservations;
      this.reservationResultsDialog = true;
    },
    stopHistoryPolling() {
      if (this.historyPollTimer !== null) {
        clearTimeout(this.historyPollTimer);
        this.historyPollTimer = null;
      }
    },
    scheduleNextHistoryPoll() {
      if (!this.showHistoryDialogVisible) {
        this.stopHistoryPolling();
        return;
      }
      if (this.historyPollCount >= HISTORY_POLL_MAX) {
        this.stopHistoryPolling();
        return;
      }
      const delayMs = this.historyPollCount < HISTORY_FAST_TICKS ? HISTORY_FAST_INTERVAL_MS : HISTORY_SLOW_INTERVAL_MS;
      this.historyPollTimer = setTimeout(() => {
        this.tickHistoryPoll()
          .then(() => {
            this.scheduleNextHistoryPoll();
          })
          .catch(() => {});
      }, delayMs);
    },
    startHistoryPolling() {
      this.stopHistoryPolling();
      this.historyPollCount = 0;
      this.scheduleNextHistoryPoll();
    },
    async tickHistoryPoll() {
      if (!this.showHistoryDialogVisible || this.historyPollCount >= HISTORY_POLL_MAX) {
        this.stopHistoryPolling();
        return;
      }
      this.historyPollCount += 1;

      try {
        const history = await store.dispatch('getReservationHistory', undefined);
        if (history && this.showHistoryDialogVisible) this.history = history;
      } catch (e) {
        console.error('tickHistoryPoll error: ', e);
      }
    },
    onHistoryDialogClose() {
      this.stopHistoryPolling();
      this.showHistoryDialogVisible = false;
    },
    async showHistory() {
      const history = await performDispatchAction<History>('getReservationHistory', undefined);
      if (!history) return;
      this.history = history;
      this.showHistoryDialogVisible = true;
      this.startHistoryPolling();
    },
    async resendConfirmation() {
      const ok = await performDispatchAction<boolean>('resendReservationConfirmation', { reservationId: this.r.id });
      if (ok) {
        this.$toast.success(this.$i18n.tc('message.message_successfuly_sent'));
      }
    },

    async onResendConfirmationFromHistory() {
      const ok = await performDispatchAction<boolean>('resendReservationConfirmation', undefined);
      if (!ok) return;
      const history = await performDispatchAction<History>('getReservationHistory', undefined);
      if (!history) return;
      this.history = history;
      this.startHistoryPolling();
    },

    // Autocomplete
    updateName(val: string) {
      this.r.contact.name = val;
    },
    updateEmail(val: string) {
      this.r.contact.email = val;
    },
    updatePhone(val: string) {
      this.r.contact.phone = val;
    },
    updateContact(contact: Contact) {
      this.$tstore.dispatch('setReservationContact', contact);
    },
    clearContactClicked() {
      if (this.isNew) {
        this.clearContact();
      } else {
        this.clearContactConfirmationVisible = true;
      }
    },
    // In Option
    fmtInOptionDate(d: Date) {
      return this.$localized.mediumDateTimeText(d);
    },
  },
});
</script>

<style lang="scss">

  #reservation-scroller {
    overflow: scroll;
  }

  .reservation-container {
    padding-top: 64px !important;
    padding-bottom: 64px !important;
    min-height: 100%
  }

</style>

<style lang="scss" scoped>
  .warning-hint-capable ::v-deep(.v-messages:not(.error--text) .v-messages__message) {
    color:$orange-color !important;
  }

  .reservation-type-picker {
    ::v-deep(.v-input__control) {
      height: 36px;
      font-size: 17px;
    }
    ::v-deep(.v-select__selections ) {
      line-height:20px;
    }

    ::v-deep(.v-input__control fieldset) {
      border-width: 1px !important;
    }

    ::v-deep(.v-input__control .v-input__slot) {
      min-height: 30px !important;
      padding-left: 4px !important;
      padding-right: 4px !important;
    }
    ::v-deep(.v-input__control .v-input__icon) {
      line-height: 24px;
      padding-bottom: 3px;
    }
    ::v-deep(fieldset) {
      .theme--dark & {
        border-color: rgba(255, 255, 255, 0.12);
      }
      border-color: rgba(0, 0, 0, 0.12);
    }
    ::v-deep(.v-select__selection) {
      padding-left: 22px;
      margin-left: auto;
      margin-right: auto;
      text-align: center;
      max-width: 100% !important;
      width: 100% !important;
    }
  }

.res-chip-wrapper {
  background-color: #f5f5f5 !important;
  margin-left: -12px!important;
  margin-right: -12px!important;
  display: flex;
  place-content: center;
  margin-top: -13px!important;
  padding-bottom: 8px!important;

  .theme--dark & {
    background-color: rgb(23, 23, 33) !important;
  }
}

</style>
